import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { forgotPassword } from '../services/authService';
import './Auth.css';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const emailTrimmed = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailTrimmed || !emailRegex.test(emailTrimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);
      const res = await forgotPassword(emailTrimmed);
      if (res.success) {
        setMessage(res.message || 'If an account exists with this email address, a password reset link has been dispatched.');
      } else {
        setError(res.message || 'Failed to send password reset link. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password | Durbhasi Gurukulam Courses</title>
        <meta name="description" content="Reset your Durbhasi Gurukulam student account password." />
      </Helmet>

      <div className="auth-page-wrapper">
        <div className="auth-card-container">
          {/* Left Brand Panel */}
          <div className="auth-brand-panel">
            <div>
              <div className="auth-brand-header">
                <img
                  src="/durbhasi-gurukulam-logo.png"
                  alt="Durbhasi Gurukulam"
                  className="auth-brand-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div>
                  <h1 className="auth-brand-title">Durbhasi <span>Gurukulam</span></h1>
                  <span className="auth-brand-tagline">Systems &amp; Software Engineering</span>
                </div>
              </div>

              <div className="auth-hero-copy">
                <h2>Account Recovery</h2>
                <p>
                  Forgot your password? No worries. Enter your registered email address and we will dispatch a secure 15-minute reset link.
                </p>

                <ul className="auth-perks-list">
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">🔒</span>
                    <span>Single-Use Cryptographic Reset Token</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">⏳</span>
                    <span>15-Minute Expiration Security</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">🛡️</span>
                    <span>Account Credential Protection</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="auth-brand-footer">
              🔒 256-Bit Encrypted Session • Durbhasi Gurukulam Portal
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="auth-form-panel">
            <span className="auth-form-kicker">Account Security</span>
            <h2 className="auth-form-title">Reset Your Password</h2>
            <p className="auth-form-subtext">
              Remember your password?{' '}
              <Link to="/login">
                Back to Sign In &rarr;
              </Link>
            </p>

            {message && (
              <div
                style={{
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#065f46',
                  fontSize: '0.88rem'
                }}
              >
                <span>✓</span>
                <span>{message}</span>
              </div>
            )}

            {error && (
              <div className="auth-error-alert">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label className="auth-input-label">
                  Registered Email Address <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="student@example.com"
                    className="auth-input-field"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
                <span className="auth-helper-text" style={{ marginTop: '6px', fontSize: '0.8rem', color: '#64748b' }}>
                  We will send a secure password reset link to this address.
                </span>
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? 'Dispatching Reset Link...' : 'Send Password Reset Link →'}
              </button>
            </form>

            <div className="auth-card-footer">
              <span>Need help accessing your account? </span>
              <Link to="/support">Contact Support Desk</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
